// @ts-strict-ignore
import React, { useEffect, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import capitalize from 'lodash-es/capitalize'
import isEmpty from 'lodash-es/isEmpty'
import { SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Avatar, toast } from '@node-space/ui-components'
import { TAG_ENABLED_NETWORKS } from 'constants/General'
import { useFormFields } from 'hooks/forms/useFormFields'
import { yupFormResolver } from 'hooks/forms/yupFormResolver'
import { useAddBeneficiaryMutationChallengeRequest } from 'hooks/mutations/useBeneficiariesMutation'
import { usePostContactInstrumentMutation } from 'hooks/mutations/usePostContactInstrumentMutation'
import { usePutContactInstrumentMutation } from 'hooks/mutations/usePutContactInstrumentMutation'
import { useCryptoCurrenciesQuery } from 'hooks/queries/useCryptoCurrenciesQuery'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { useSupportedCurrencies } from 'hooks/useSupportedCurrencies'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { BaseErrorResponse, ChallengeRequest } from 'types/beneficiaries'
import {
  ContactInstrument,
  ContactInstrumentPayload,
  ContactInstrumentProps,
  InstrumentType,
} from 'types/contacts'
import { logSentryErrorAndGetTraceId } from 'utils/sentry'
import {
  contactAtom,
  contactInstrumentActionAtom,
  contactInstrumentAtom,
  editingContactInstrumentAtom,
} from '../atoms/contactAtoms'
import {
  ContactInstrumentFormData,
  ContactInstrumentFormKeys,
  useContactInstrumentSchema,
} from '../schemas/contactInstrumentSchema'
import { getContactInstrumentDefaultValues } from '../utils/utils'

export const useContactInstrumentForm = () => {
  const { t } = useTranslation()
  const { getIsSupportedCurrency } = useSupportedCurrencies()
  const queryClient = useQueryClient()
  const contact = useAtomValue(contactAtom)
  const [contactInstrumentAction, setContactInstrumentAction] = useAtom(contactInstrumentActionAtom)

  const [contactInstrument, setContactInstrument] = useAtom(contactInstrumentAtom)
  const setIsEditingContactInstrument = useSetAtom(editingContactInstrumentAtom)
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()

  const { mutate: postContactInstrument, isPending: isPostingContactInstrument } =
    usePostContactInstrumentMutation()
  const { mutate: putContactInstrument, isPending: isUpdatingContactInstrument } =
    usePutContactInstrumentMutation()
  const { mutate: mutatePostChallengeRequest, isPending: isPostingChallengeRequest } =
    useAddBeneficiaryMutationChallengeRequest()
  const { data: cryptoCurrencies, isPending: isLoadingCryptoCurrencies } =
    useCryptoCurrenciesQuery()

  const cryptoCurrenciesOptions = useMemo(() => {
    if (cryptoCurrencies?.length) {
      return cryptoCurrencies
        ?.filter(currency => getIsSupportedCurrency(currency))
        ?.flatMap(cryptoCurrency => {
          return cryptoCurrency?.protocols?.map(protocol => {
            const protocolName =
              cryptoCurrency?.protocols?.length > 1
                ? `${cryptoCurrency?.code?.toLowerCase()}-${protocol?.code?.toLowerCase()}`
                : cryptoCurrency?.code?.toLowerCase()
            return {
              leftText: `${cryptoCurrency?.code} (${capitalize(protocol?.networkCode)})`,
              value: `${cryptoCurrency?.code} (${protocol?.networkCode})`,
              currencyCode: cryptoCurrency?.code,
              networkCode: protocol?.networkCode,
              withdrawalParameters: cryptoCurrency?.withdrawalParameters,
              LeftElement: <Avatar name={protocolName} size="small" />,
            }
          })
        })
    }
    return []
  }, [cryptoCurrencies])

  const formSchema = useContactInstrumentSchema(t)

  const isEditingContactInstrument =
    !isEmpty(contactInstrument) && !contactInstrumentAction?.meta?.handleContactInstrumentUpdate

  useEffect(() => {
    setIsEditingContactInstrument(isEditingContactInstrument)
  }, [isEditingContactInstrument])

  const form = useForm<ContactInstrumentFormData>({
    defaultValues: getContactInstrumentDefaultValues(contactInstrument),
    resolver: yupFormResolver<ContactInstrumentFormData>(formSchema),
  })

  const {
    control,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors, isSubmitting },
  } = form

  const formValues = useWatch({ control })

  const { setInputField, setComboboxField, setBaseField, setCheckboxField } =
    useFormFields<ContactInstrumentFormKeys>(formSchema, form, formValues)

  const formFields: { [key in ContactInstrumentFormKeys] } = {
    description: setInputField({
      key: 'description',
      placeholder: t('formFields.placeholder.addressDescription'),
    }),
    currencyAndNetwork: setComboboxField({
      key: 'currencyAndNetwork',
      items: cryptoCurrenciesOptions,
      placeholder: t('select'),
      defaultValue: watch('currencyAndNetwork'),
    }),
    walletAddress: setInputField({
      key: 'walletAddress',
    }),
    tag: setInputField({
      key: 'tag',
    }),
    selfHosted: setCheckboxField({
      key: 'selfHosted',
      name: 'selfHosted',
      id: 'selfHosted',
      testId: 'self-hosted',
    }),
    otp: setBaseField({
      key: 'otp',
    }),
  }

  const handleOnError = (error: BaseErrorResponse, message: string) => {
    if (error?.status === 428) {
      postChallengeRequest(error)
      return
    }

    const sentryTraceId = logSentryErrorAndGetTraceId(
      `Failed API request to /platform/v1/contacts/{contactId}/instruments with status ${error?.status} - ${message}`,
      error
    )

    setRequestError({
      handledMessage: error?.message,
      errorCode: error?.status,
      sentryTraceId,
      show: true,
    })
  }

  const selectedCryptoCurrencyOption = useMemo(
    () =>
      cryptoCurrenciesOptions?.find(
        cryptoCurrencyOption => cryptoCurrencyOption?.value === formValues?.currencyAndNetwork
      ),
    [cryptoCurrenciesOptions, formValues?.currencyAndNetwork]
  )

  const withdrawalParamCode = selectedCryptoCurrencyOption?.withdrawalParameters?.[0]?.code

  const isStellarMemo =
    selectedCryptoCurrencyOption?.networkCode?.toLowerCase() ===
    TAG_ENABLED_NETWORKS.STELLAR.toLowerCase()

  const hasDestinationTag =
    ['destinationTag', 'note'].includes(withdrawalParamCode) || isStellarMemo

  const onSubmit: SubmitHandler<typeof formValues> = () => {
    resetRequestError()
    const instrumentPayload: ContactInstrumentPayload = {
      type: InstrumentType.CRYPTO,
      address: formValues?.walletAddress,
      network: selectedCryptoCurrencyOption?.networkCode,
      currency: selectedCryptoCurrencyOption?.currencyCode,
      description: formValues?.description,
      ...(formValues?.tag && hasDestinationTag && { tag: formValues?.tag }),
      ...(formValues?.selfHosted && { selfHosted: formValues?.selfHosted }),
    }
    const instrumentRequestPayload: ContactInstrumentProps = {
      ...(isEditingContactInstrument && { instrumentId: contactInstrument?.id }),
      contactId: contact?.id,
      instrument: instrumentPayload,
    }
    if (!isEditingContactInstrument) {
      postContactInstrument(instrumentRequestPayload, {
        onError: (error: BaseErrorResponse) => {
          handleOnError(error, 'postContactInstrument')
        },
      })
    } else {
      putContactInstrument(instrumentRequestPayload, {
        onError: (error: BaseErrorResponse) => {
          handleOnError(error, 'putContactInstrument')
        },
      })
    }
  }

  const postChallengeRequest = (error: BaseErrorResponse) => {
    const data: ChallengeRequest = {
      challenge: error?.data?.challenge?.value,
      tokenResponse: formValues?.otp,
      mfaCallbackUrl: error?.data?.callbackUrl,
    }
    mutatePostChallengeRequest(data, {
      onSuccess: (contactInstrument: ContactInstrument) => {
        toast({
          header: t(
            isEditingContactInstrument
              ? 'contacts.updateContactInstrumentSuccess'
              : 'contacts.addContactInstrumentSuccess',
            {
              description: formValues?.description,
            }
          ),
          variant: 'success',
        })
        if (formValues?.selfHosted) {
          toast({
            header: t('contacts.youCannotSendFundsBeforeVerify', { name: formValues?.description }),
            variant: 'warning',
            showCloseButton: true,
          })
        }

        contactInstrumentAction?.meta?.handleContactInstrumentUpdate?.(contactInstrument, contact)
        setContactInstrument(null)
        queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACT_INSTRUMENTS] })
        queryClient.invalidateQueries({
          queryKey: [reactQueryName.WALLET_CONTACT_PAYMENT_INSTRUMENTS],
        })
        contactInstrumentAction?.onClose()
        setContactInstrumentAction(null)
      },
      onError: (error: BaseErrorResponse) => {
        const errorStatus = error?.status
        const errorDetails = error?.data?.details?.errors ?? null
        const errorMessage = error?.data?.message
        const isPermissionError = errorStatus === 403

        if (isPermissionError) {
          setError('otp', { type: 'custom', message: t('incorrect2FaRetry') })
          return
        }

        const sentryTraceId = logSentryErrorAndGetTraceId(
          `Failed API request to /api/beneficiaries/operation-authorizations with status ${error?.status} - postChallengeRequest - contact instrument`,
          error,
          {
            data: typeof data === 'string' ? { message: data } : data,
          }
        )

        setRequestError({
          errorCode: error?.status,
          sentryTraceId,
          handledMessage: errorMessage,
          ErrorMessages: errorDetails,
          show: true,
        })
      },
    })
  }

  return {
    formFields,
    isLoadingCryptoCurrencies,
    errors,
    isSubmitting,
    isPostingContactInstrument,
    isPostingChallengeRequest,
    isEditingContactInstrument,
    isUpdatingContactInstrument,
    requestError,
    hasDestinationTag,
    isStellarMemo,
    handleSubmit,
    onSubmit,
    setValue,
    clearErrors,
  }
}
